/**
 * Everything needed to build a playable level-1 character of each class,
 * 2024 rules. SRD 5.2 content (CC-BY-4.0), with descriptions written for
 * people who have never played before.
 */
import type { Ability, ClassKey, SkillKey } from '../constants'

export interface ClassChoice {
  id: string
  label: string
  help: string
  count: number
  options: { value: string; label: string; help: string }[]
}

export interface EquipmentOption {
  id: string
  label: string
  /** Plain items written straight onto the sheet. */
  items: string[]
  /** Packs get expanded into their contents. */
  packs?: string[]
  /** Ids from equipment.ts, used to build the attack table and AC. */
  weaponIds: string[]
  armorId?: string
  shield?: boolean
  gold: number
}

export interface ClassBuild {
  id: ClassKey
  name: string
  tagline: string
  blurb: string
  /** Three short bullets: what you actually do on your turn. */
  playsLike: string[]
  complexity: 'simple' | 'medium' | 'complex'
  primaryAbility: Ability[]
  hitDie: number
  saves: [Ability, Ability]
  skillChoices: { count: number; from: SkillKey[] }
  armorTraining: { light: boolean; medium: boolean; heavy: boolean; shields: boolean }
  weaponTraining: string
  toolTraining?: string
  equipmentOptions: EquipmentOption[]
  level1Features: { name: string; text: string }[]
  choices: ClassChoice[]
  spellcasting?: {
    ability: Ability
    cantrips: number
    prepared: number
    /** Wizards learn more than they prepare. */
    spellbook?: number
    note: string
  }
  /** Number of weapons that get a Mastery property at level 1. */
  weaponMastery?: number
}

const ALL_SKILLS: SkillKey[] = [
  'acrobatics', 'animal_handling', 'arcana', 'athletics', 'deception', 'history',
  'insight', 'intimidation', 'investigation', 'medicine', 'nature', 'perception',
  'performance', 'persuasion', 'religion', 'sleight_of_hand', 'stealth', 'survival',
]

const FIGHTING_STYLES: ClassChoice['options'] = [
  { value: 'archery', label: 'Archery', help: '+2 to hit with ranged weapons. Simple and strong if you use a bow.' },
  { value: 'defense', label: 'Defense', help: '+1 AC while wearing armour. Never a wasted pick.' },
  { value: 'dueling', label: 'Dueling', help: '+2 damage with a one-handed weapon and no second weapon. Pairs with a shield.' },
  { value: 'great-weapon', label: 'Great Weapon Fighting', help: 'Reroll 1s and 2s on damage with two-handed weapons.' },
  { value: 'protection', label: 'Protection', help: 'Use your reaction to impose Disadvantage on an attack against a nearby ally.' },
  { value: 'two-weapon', label: 'Two-Weapon Fighting', help: 'Add your ability modifier to the damage of your off-hand attack.' },
  { value: 'blind-fighting', label: 'Blind Fighting', help: 'Blindsight 10 feet — you can fight in magical darkness.' },
  { value: 'unarmed', label: 'Unarmed Fighting', help: 'Your unarmed strikes hit for 1d6, or 1d8 with nothing in your hands.' },
]

export const CLASS_BUILDS: ClassBuild[] = [
  {
    id: 'fighter',
    name: 'Fighter',
    tagline: 'The most straightforward way to be very good at fighting.',
    blurb:
      'You hit things, you can take a hit, and you wear the best armour in the game. The easiest class to learn — your turn is usually "attack", and it works.',
    playsLike: ['Attack with a weapon every turn', 'Heal yourself once per rest with Second Wind', 'Wear heavy armour and carry a shield'],
    complexity: 'simple',
    primaryAbility: ['str', 'dex'],
    hitDie: 10,
    saves: ['str', 'con'],
    skillChoices: {
      count: 2,
      from: ['acrobatics', 'animal_handling', 'athletics', 'history', 'insight', 'intimidation', 'perception', 'persuasion', 'survival'],
    },
    armorTraining: { light: true, medium: true, heavy: true, shields: true },
    weaponTraining: 'Simple and Martial weapons',
    weaponMastery: 3,
    equipmentOptions: [
      {
        id: 'melee', label: 'Heavy armour and a greatsword',
        items: ['Chain Mail', 'Greatsword', 'Flail', '8 Javelins'], packs: ["Dungeoneer's Pack"],
        weaponIds: ['greatsword', 'flail', 'javelin'], armorId: 'chain-mail', gold: 4,
      },
      {
        id: 'ranged', label: 'Light armour, blades, and a longbow',
        items: ['Studded Leather Armor', 'Scimitar', 'Shortsword', 'Longbow', '20 Arrows'], packs: ["Dungeoneer's Pack"],
        weaponIds: ['scimitar', 'shortsword', 'longbow'], armorId: 'studded-leather', gold: 11,
      },
      { id: 'gold', label: 'Just take the gold (155 GP)', items: [], weaponIds: [], gold: 155 },
    ],
    level1Features: [
      { name: 'Second Wind', text: 'As a Bonus Action, regain 1d10 + your Fighter level hit points. Two uses per Short Rest.' },
      { name: 'Weapon Mastery', text: 'You unlock the mastery property of three weapons you are proficient with.' },
    ],
    choices: [
      { id: 'fightingStyle', label: 'Fighting Style', help: 'A permanent perk that shapes how you fight. Defense is the safe default.', count: 1, options: FIGHTING_STYLES },
    ],
  },
  {
    id: 'barbarian',
    name: 'Barbarian',
    tagline: 'Rage. Enormous hit points. Very hard to put down.',
    blurb:
      'You take half damage from most attacks while raging and hit harder than anyone. No spells, no bookkeeping — just the highest hit points in the game.',
    playsLike: ['Rage as a Bonus Action, then attack', 'Take half damage from weapons while raging', 'Wear light armour and rely on Constitution'],
    complexity: 'simple',
    primaryAbility: ['str'],
    hitDie: 12,
    saves: ['str', 'con'],
    skillChoices: { count: 3, from: ['animal_handling', 'athletics', 'intimidation', 'nature', 'perception', 'survival'] },
    armorTraining: { light: true, medium: true, heavy: false, shields: true },
    weaponTraining: 'Simple and Martial weapons',
    weaponMastery: 2,
    equipmentOptions: [
      {
        id: 'standard', label: 'Greataxe and handaxes',
        items: ['Greataxe', '4 Handaxes'], packs: ["Explorer's Pack"],
        weaponIds: ['greataxe', 'handaxe'], gold: 15,
      },
      { id: 'gold', label: 'Just take the gold (75 GP)', items: [], weaponIds: [], gold: 75 },
    ],
    level1Features: [
      { name: 'Rage', text: 'As a Bonus Action, enter a Rage for 10 minutes: bonus damage on Strength attacks, resistance to Bludgeoning, Piercing, and Slashing damage, and advantage on Strength checks and saves. Two uses per Long Rest.' },
      { name: 'Unarmored Defense', text: 'While not wearing armour, your AC equals 10 + your Dexterity modifier + your Constitution modifier. You can still use a shield.' },
      { name: 'Weapon Mastery', text: 'You unlock the mastery property of two weapons you are proficient with.' },
    ],
    choices: [],
  },
  {
    id: 'rogue',
    name: 'Rogue',
    tagline: 'Sneak Attack, lockpicking, and the best skills at the table.',
    blurb:
      'You deal a burst of extra damage when you have advantage or an ally nearby, and you are better at skills than anyone else. Great for players who like solving problems sideways.',
    playsLike: ['Attack with a finesse weapon for Sneak Attack damage', 'Dash, Disengage, or Hide as a Bonus Action', 'Be the best at Stealth and Thieves\' Tools'],
    complexity: 'medium',
    primaryAbility: ['dex'],
    hitDie: 8,
    saves: ['dex', 'int'],
    skillChoices: {
      count: 4,
      from: ['acrobatics', 'athletics', 'deception', 'insight', 'intimidation', 'investigation', 'perception', 'persuasion', 'sleight_of_hand', 'stealth'],
    },
    armorTraining: { light: true, medium: false, heavy: false, shields: false },
    weaponTraining: 'Simple weapons, plus Martial weapons that have the Finesse or Light property',
    toolTraining: "Thieves' Tools",
    weaponMastery: 2,
    equipmentOptions: [
      {
        id: 'standard', label: 'Leather armour, blades, and a bow',
        items: ['Leather Armor', '2 Daggers', 'Shortsword', 'Shortbow', '20 Arrows', "Thieves' Tools"], packs: ["Burglar's Pack"],
        weaponIds: ['dagger', 'shortsword', 'shortbow'], armorId: 'leather', gold: 8,
      },
      { id: 'gold', label: 'Just take the gold (100 GP)', items: [], weaponIds: [], gold: 100 },
    ],
    level1Features: [
      { name: 'Sneak Attack', text: 'Once per turn, deal an extra 1d6 damage when you hit with a Finesse or Ranged weapon and either have Advantage or an ally is next to the target.' },
      { name: "Thieves' Cant", text: 'You know a secret mix of dialect, jargon, and code that lets you hide messages in ordinary conversation.' },
      { name: 'Weapon Mastery', text: 'You unlock the mastery property of two weapons you are proficient with.' },
    ],
    choices: [
      {
        id: 'expertise', label: 'Expertise', help: 'Pick two of your skills. Your proficiency bonus doubles for them — this is what makes rogues the best at what they do.',
        count: 2, options: [],
      },
    ],
  },
  {
    id: 'cleric',
    name: 'Cleric',
    tagline: 'Divine magic, healing, and surprisingly good armour.',
    blurb:
      'You heal, buff, and can still hold a front line. Clerics prepare their spells fresh each day, so you are never stuck with the wrong list.',
    playsLike: ['Cast a spell or swing a mace', 'Heal allies and remove conditions', 'Channel Divinity from level 2'],
    complexity: 'medium',
    primaryAbility: ['wis'],
    hitDie: 8,
    saves: ['wis', 'cha'],
    skillChoices: { count: 2, from: ['history', 'insight', 'medicine', 'persuasion', 'religion'] },
    armorTraining: { light: true, medium: true, heavy: false, shields: true },
    weaponTraining: 'Simple weapons',
    equipmentOptions: [
      {
        id: 'standard', label: 'Chain shirt, shield, and mace',
        items: ['Chain Shirt', 'Shield', 'Mace', 'Holy Symbol'], packs: ["Priest's Pack"],
        weaponIds: ['mace'], armorId: 'chain-shirt', shield: true, gold: 7,
      },
      { id: 'gold', label: 'Just take the gold (110 GP)', items: [], weaponIds: [], gold: 110 },
    ],
    level1Features: [
      { name: 'Spellcasting', text: 'You cast Cleric spells using Wisdom. You prepare a fresh list of spells after each Long Rest.' },
    ],
    choices: [
      {
        id: 'divineOrder', label: 'Divine Order', help: 'What kind of cleric you are.', count: 1,
        options: [
          { value: 'protector', label: 'Protector', help: 'Training with Martial weapons and Heavy armour. Play near the front.' },
          { value: 'thaumaturge', label: 'Thaumaturge', help: 'An extra cantrip and a bonus to Arcana and Religion checks. Play as a caster.' },
        ],
      },
    ],
    spellcasting: { ability: 'wis', cantrips: 3, prepared: 4, note: 'You prepare 4 spells after each Long Rest and can change them freely.' },
  },
  {
    id: 'wizard',
    name: 'Wizard',
    tagline: 'The widest spell list in the game, from a book you keep filling.',
    blurb:
      'Fragile in a fight but capable of solving almost any problem with the right spell. Best for players who enjoy having options.',
    playsLike: ['Cast a spell nearly every turn', 'Keep a spellbook and prepare from it daily', 'Stay out of melee — you have few hit points'],
    complexity: 'complex',
    primaryAbility: ['int'],
    hitDie: 6,
    saves: ['int', 'wis'],
    skillChoices: { count: 2, from: ['arcana', 'history', 'insight', 'investigation', 'medicine', 'nature', 'religion'] },
    armorTraining: { light: false, medium: false, heavy: false, shields: false },
    weaponTraining: 'Simple weapons',
    equipmentOptions: [
      {
        id: 'standard', label: 'Staff, focus, and spellbook',
        items: ['Quarterstaff', 'Arcane Focus (staff)', 'Robe', 'Spellbook'], packs: ["Scholar's Pack"],
        weaponIds: ['quarterstaff'], gold: 5,
      },
      { id: 'gold', label: 'Just take the gold (55 GP)', items: [], weaponIds: [], gold: 55 },
    ],
    level1Features: [
      { name: 'Spellcasting', text: 'You cast Wizard spells using Intelligence, preparing them from your spellbook after a Long Rest.' },
      { name: 'Ritual Adept', text: 'You can cast any spell in your spellbook with the Ritual tag as a ritual without preparing it.' },
      { name: 'Arcane Recovery', text: 'Once per day after a Short Rest, recover expended spell slots totalling half your wizard level (rounded up).' },
    ],
    choices: [],
    spellcasting: { ability: 'int', cantrips: 3, prepared: 4, spellbook: 6, note: 'Your spellbook holds 6 spells; you prepare 4 of them each day.' },
  },
  {
    id: 'bard',
    name: 'Bard',
    tagline: 'Magic, skills, and making everyone else better at their job.',
    blurb:
      'You buff allies with Bardic Inspiration, talk your way past problems, and cast from one of the most flexible spell lists. Excellent in a social, story-driven campaign.',
    playsLike: ['Hand out Bardic Inspiration dice as a Bonus Action', 'Cast support and control spells', 'Be the face of the party'],
    complexity: 'medium',
    primaryAbility: ['cha'],
    hitDie: 8,
    saves: ['dex', 'cha'],
    skillChoices: { count: 3, from: ALL_SKILLS },
    armorTraining: { light: true, medium: false, heavy: false, shields: false },
    weaponTraining: 'Simple weapons',
    toolTraining: 'Three Musical Instruments of your choice',
    equipmentOptions: [
      {
        id: 'standard', label: 'Leather armour, dagger, and an instrument',
        items: ['Leather Armor', 'Dagger', 'Musical Instrument'], packs: ["Entertainer's Pack"],
        weaponIds: ['dagger'], armorId: 'leather', gold: 19,
      },
      { id: 'gold', label: 'Just take the gold (90 GP)', items: [], weaponIds: [], gold: 90 },
    ],
    level1Features: [
      { name: 'Bardic Inspiration', text: 'As a Bonus Action, give a creature a d6 they can add to a d20 Test or damage roll within the hour. Uses equal to your Charisma modifier, regained on a Short Rest.' },
      { name: 'Spellcasting', text: 'You cast Bard spells using Charisma.' },
    ],
    choices: [],
    spellcasting: { ability: 'cha', cantrips: 2, prepared: 4, note: 'You know 4 spells and can swap one whenever you gain a level.' },
  },
  {
    id: 'druid',
    name: 'Druid',
    tagline: 'Nature magic, and turning into animals.',
    blurb:
      'You control the battlefield with primal magic and can Wild Shape into beasts from level 2. Thematically perfect for a Feywild story.',
    playsLike: ['Cast nature and control spells', 'Wild Shape into animals (from level 2)', 'Talk to plants and beasts'],
    complexity: 'complex',
    primaryAbility: ['wis'],
    hitDie: 8,
    saves: ['int', 'wis'],
    skillChoices: { count: 2, from: ['arcana', 'animal_handling', 'insight', 'medicine', 'nature', 'perception', 'religion', 'survival'] },
    armorTraining: { light: true, medium: false, heavy: false, shields: true },
    weaponTraining: 'Simple weapons',
    toolTraining: 'Herbalism Kit',
    equipmentOptions: [
      {
        id: 'standard', label: 'Leather armour, shield, and a druidic focus',
        items: ['Leather Armor', 'Shield', 'Sickle', 'Druidic Focus (quarterstaff)', 'Herbalism Kit'], packs: ["Explorer's Pack"],
        weaponIds: ['sickle', 'quarterstaff'], armorId: 'leather', shield: true, gold: 9,
      },
      { id: 'gold', label: 'Just take the gold (50 GP)', items: [], weaponIds: [], gold: 50 },
    ],
    level1Features: [
      { name: 'Spellcasting', text: 'You cast Druid spells using Wisdom, preparing a fresh list after each Long Rest.' },
      { name: 'Druidic', text: 'You know Druidic, the secret language of druids, and can leave hidden messages in it.' },
    ],
    choices: [
      {
        id: 'primalOrder', label: 'Primal Order', help: 'What kind of druid you are.', count: 1,
        options: [
          { value: 'magician', label: 'Magician', help: 'An extra cantrip and a bonus to Arcana and Nature checks. Lean into spellcasting.' },
          { value: 'warden', label: 'Warden', help: 'Training with Martial weapons and Medium armour. Stand closer to the fight.' },
        ],
      },
    ],
    spellcasting: { ability: 'wis', cantrips: 2, prepared: 4, note: 'You prepare 4 spells after each Long Rest and can change them freely.' },
  },
  {
    id: 'paladin',
    name: 'Paladin',
    tagline: 'Heavy armour, healing hands, and enormous burst damage.',
    blurb:
      'You are a front-line fighter who can heal, cure poison, and dump spell slots into devastating Divine Smites. Tough and forgiving to play.',
    playsLike: ['Attack in heavy armour with a shield', 'Heal with Lay On Hands, no spell slot needed', 'Spend spell slots for extra radiant damage'],
    complexity: 'medium',
    primaryAbility: ['str', 'cha'],
    hitDie: 10,
    saves: ['wis', 'cha'],
    skillChoices: { count: 2, from: ['athletics', 'insight', 'intimidation', 'medicine', 'persuasion', 'religion'] },
    armorTraining: { light: true, medium: true, heavy: true, shields: true },
    weaponTraining: 'Simple and Martial weapons',
    equipmentOptions: [
      {
        id: 'standard', label: 'Chain mail, shield, and a longsword',
        items: ['Chain Mail', 'Shield', 'Longsword', '6 Javelins', 'Holy Symbol'], packs: ["Priest's Pack"],
        weaponIds: ['longsword', 'javelin'], armorId: 'chain-mail', shield: true, gold: 9,
      },
      { id: 'gold', label: 'Just take the gold (150 GP)', items: [], weaponIds: [], gold: 150 },
    ],
    level1Features: [
      { name: 'Lay On Hands', text: 'You have a pool of healing equal to five times your Paladin level. As a Bonus Action, touch a creature to spend from it, or spend 5 points to end one disease or the Poisoned condition.' },
      { name: 'Spellcasting', text: 'You cast Paladin spells using Charisma, preparing a fresh list after each Long Rest.' },
    ],
    choices: [],
    spellcasting: { ability: 'cha', cantrips: 0, prepared: 2, note: 'You prepare 2 spells after each Long Rest. Paladins get no cantrips.' },
  },
  {
    id: 'ranger',
    name: 'Ranger',
    tagline: 'A skilled hunter with a bow, a blade, and a little magic.',
    blurb:
      'You track, scout, and mark a target for extra damage every turn. A solid mix of fighting and utility without much bookkeeping.',
    playsLike: ['Mark a target with Hunter\'s Mark for extra damage', 'Attack at range or with two blades', 'Be the party\'s tracker and scout'],
    complexity: 'medium',
    primaryAbility: ['dex', 'wis'],
    hitDie: 10,
    saves: ['str', 'dex'],
    skillChoices: { count: 3, from: ['animal_handling', 'athletics', 'insight', 'investigation', 'nature', 'perception', 'stealth', 'survival'] },
    armorTraining: { light: true, medium: true, heavy: false, shields: true },
    weaponTraining: 'Simple and Martial weapons',
    weaponMastery: 2,
    equipmentOptions: [
      {
        id: 'standard', label: 'Studded leather, blades, and a longbow',
        items: ['Studded Leather Armor', 'Scimitar', 'Shortsword', 'Longbow', '20 Arrows', 'Druidic Focus (sprig of mistletoe)'], packs: ["Explorer's Pack"],
        weaponIds: ['scimitar', 'shortsword', 'longbow'], armorId: 'studded-leather', gold: 7,
      },
      { id: 'gold', label: 'Just take the gold (150 GP)', items: [], weaponIds: [], gold: 150 },
    ],
    level1Features: [
      { name: 'Favored Enemy', text: "You always have Hunter's Mark prepared, and can cast it twice per Long Rest without a spell slot." },
      { name: 'Spellcasting', text: 'You cast Ranger spells using Wisdom.' },
      { name: 'Weapon Mastery', text: 'You unlock the mastery property of two weapons you are proficient with.' },
    ],
    choices: [],
    spellcasting: { ability: 'wis', cantrips: 0, prepared: 2, note: 'You know 2 spells. Rangers get no cantrips.' },
  },
  {
    id: 'sorcerer',
    name: 'Sorcerer',
    tagline: 'Raw innate magic you bend on the fly.',
    blurb:
      'Fewer spells than a wizard, but you can twist them — casting two at once, at longer range, or without components. Very powerful, a bit more to track.',
    playsLike: ['Cast damage and control spells', 'Spend Sorcery Points to modify spells (from level 2)', 'Stay at range — you are fragile'],
    complexity: 'complex',
    primaryAbility: ['cha'],
    hitDie: 6,
    saves: ['con', 'cha'],
    skillChoices: { count: 2, from: ['arcana', 'deception', 'insight', 'intimidation', 'persuasion', 'religion'] },
    armorTraining: { light: false, medium: false, heavy: false, shields: false },
    weaponTraining: 'Simple weapons',
    equipmentOptions: [
      {
        id: 'standard', label: 'Spear, daggers, and an arcane focus',
        items: ['Spear', '2 Daggers', 'Arcane Focus (crystal)'], packs: ["Dungeoneer's Pack"],
        weaponIds: ['spear', 'dagger'], gold: 28,
      },
      { id: 'gold', label: 'Just take the gold (50 GP)', items: [], weaponIds: [], gold: 50 },
    ],
    level1Features: [
      { name: 'Spellcasting', text: 'You cast Sorcerer spells using Charisma.' },
      { name: 'Innate Sorcery', text: 'As a Bonus Action, for 1 minute your spell save DC increases by 1 and you have Advantage on Sorcerer spell attack rolls. Twice per Long Rest.' },
    ],
    choices: [],
    spellcasting: { ability: 'cha', cantrips: 4, prepared: 2, note: 'You know 2 spells and can swap one when you level up.' },
  },
  {
    id: 'warlock',
    name: 'Warlock',
    tagline: 'A pact with something powerful, and an attack cantrip that scales.',
    blurb:
      'Few spell slots, but they always come back on a short rest and always cast at your highest level. Eldritch Blast plus Invocations makes for a strong, simple turn.',
    playsLike: ['Fire Eldritch Blast almost every turn', 'Cast your few spells at maximum power', 'Customise yourself with Eldritch Invocations'],
    complexity: 'medium',
    primaryAbility: ['cha'],
    hitDie: 8,
    saves: ['wis', 'cha'],
    skillChoices: { count: 2, from: ['arcana', 'deception', 'history', 'intimidation', 'investigation', 'nature', 'religion'] },
    armorTraining: { light: true, medium: false, heavy: false, shields: false },
    weaponTraining: 'Simple weapons',
    equipmentOptions: [
      {
        id: 'standard', label: 'Leather armour, blades, and an arcane focus',
        items: ['Leather Armor', 'Sickle', '2 Daggers', 'Arcane Focus (orb)', 'Book (occult lore)'], packs: ["Scholar's Pack"],
        weaponIds: ['sickle', 'dagger'], armorId: 'leather', gold: 15,
      },
      { id: 'gold', label: 'Just take the gold (100 GP)', items: [], weaponIds: [], gold: 100 },
    ],
    level1Features: [
      { name: 'Pact Magic', text: 'You have 1 spell slot, always cast at your highest level, and it comes back on a Short Rest as well as a Long Rest.' },
    ],
    choices: [
      {
        id: 'invocations', label: 'Eldritch Invocations', help: 'Two permanent magical perks. Agonizing Blast is the standard damage pick.', count: 2,
        options: [
          { value: 'agonizing-blast', label: 'Agonizing Blast', help: 'Add your Charisma modifier to Eldritch Blast damage. Requires Eldritch Blast.' },
          { value: 'armor-of-shadows', label: 'Armor of Shadows', help: 'Cast Mage Armor on yourself at will, no slot needed.' },
          { value: 'devils-sight', label: "Devil's Sight", help: 'See normally in darkness, magical or not, out to 120 feet.' },
          { value: 'eldritch-mind', label: 'Eldritch Mind', help: 'Advantage on Constitution saves to maintain Concentration.' },
          { value: 'mask-of-many-faces', label: 'Mask of Many Faces', help: 'Cast Disguise Self at will.' },
          { value: 'pact-of-the-blade', label: 'Pact of the Blade', help: 'Summon a magic weapon and attack with Charisma.' },
          { value: 'pact-of-the-tome', label: 'Pact of the Tome', help: 'A book granting three cantrips and two 1st-level rituals.' },
          { value: 'pact-of-the-chain', label: 'Pact of the Chain', help: 'Cast Find Familiar as a ritual, with special familiar forms.' },
        ],
      },
    ],
    spellcasting: { ability: 'cha', cantrips: 2, prepared: 2, note: 'You know 2 spells, cast through a single slot that recharges on a Short Rest.' },
  },
  {
    id: 'monk',
    name: 'Monk',
    tagline: 'Fast, unarmoured, and throws a lot of punches.',
    blurb:
      'You move further than anyone, attack multiple times a turn without a weapon, and dodge things with Focus points. Great mobility, low durability early on.',
    playsLike: ['Attack twice with unarmed strikes or a simple weapon', 'Move fast and reposition freely', 'Spend Focus points for extra effects (from level 2)'],
    complexity: 'medium',
    primaryAbility: ['dex', 'wis'],
    hitDie: 8,
    saves: ['str', 'dex'],
    skillChoices: { count: 2, from: ['acrobatics', 'athletics', 'history', 'insight', 'religion', 'stealth'] },
    armorTraining: { light: false, medium: false, heavy: false, shields: false },
    weaponTraining: 'Simple weapons and Martial weapons that have the Light property',
    toolTraining: "One type of Artisan's Tools or one Musical Instrument",
    equipmentOptions: [
      {
        id: 'standard', label: 'Spear, daggers, and a craft kit',
        items: ['Spear', '5 Daggers', "Artisan's Tools or Musical Instrument"], packs: ["Explorer's Pack"],
        weaponIds: ['spear', 'dagger'], gold: 11,
      },
      { id: 'gold', label: 'Just take the gold (50 GP)', items: [], weaponIds: [], gold: 50 },
    ],
    level1Features: [
      { name: 'Martial Arts', text: 'Your unarmed strikes deal 1d6 damage and can use Dexterity. When you attack, you can make one extra unarmed strike as a Bonus Action.' },
      { name: 'Unarmored Defense', text: 'While wearing no armour and no shield, your AC equals 10 + your Dexterity modifier + your Wisdom modifier.' },
    ],
    choices: [],
  },
]

export const CLASS_BUILDS_BY_ID = new Map(CLASS_BUILDS.map((c) => [c.id, c]))

/** Ordered so the least fiddly classes come first for new players. */
export const CLASSES_BY_COMPLEXITY = [...CLASS_BUILDS].sort((a, b) => {
  const rank = { simple: 0, medium: 1, complex: 2 }
  return rank[a.complexity] - rank[b.complexity] || a.name.localeCompare(b.name)
})
